import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme variant constants
export const THEME_VARIANTS = {
  LIGHT: 'light',
  DARK: 'dark',
  AUTO: 'auto'
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('liquidGlassTheme');
    return saved ? JSON.parse(saved) : false;
  });

  const [themeVariant, setThemeVariant] = useState(() => {
    // Check localStorage for saved variant preference
    const savedVariant = localStorage.getItem('liquidGlassThemeVariant');
    return savedVariant || THEME_VARIANTS.AUTO;
  });

  useEffect(() => {
    // Save preference to localStorage
    localStorage.setItem('liquidGlassTheme', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  useEffect(() => {
    // Save variant preference to localStorage
    localStorage.setItem('liquidGlassThemeVariant', themeVariant);
  }, [themeVariant]);

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  const setVariant = (variant) => {
    if (Object.values(THEME_VARIANTS).includes(variant)) {
      setThemeVariant(variant);
    }
  };

  // Helper function to determine effective dark mode based on variant
  const getEffectiveDarkMode = (componentVariant = THEME_VARIANTS.AUTO) => {
    if (componentVariant === THEME_VARIANTS.DARK) return true;
    if (componentVariant === THEME_VARIANTS.LIGHT) return false;
    return isDarkMode; // AUTO or fallback
  };

  const themeValue = {
    isDarkMode,
    toggleTheme,
    themeVariant,
    setVariant,
    getEffectiveDarkMode,
    // Convenience methods
    isLight: !isDarkMode,
    isDark: isDarkMode,
    // Theme variant helpers
    variants: THEME_VARIANTS
  };

  return (
    <ThemeContext.Provider value={themeValue}>
      {children}
    </ThemeContext.Provider>
  );
};
